import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconPages from '~icons/iconoir/multiple-pages';

import { useGMBTemplateHistoryQuery } from '@adflow/frontend/feat-google-my-business';
import { Avatar, EmptySection } from '@adflow/frontend/ui-common';
import type { DataTableFullProps } from '@adflow/frontend/ui-data-table';
import {
	DataTableFull,
	HeaderCell,
	TruncatedTextCell,
	useColumnTemplate,
} from '@adflow/frontend/ui-data-table';
import { theme } from '@adflow/frontend/ui-theme';
import type { SortParams } from '@adflow/frontend/util-router';
import {
	formatDateAndTime,
	sentenceCase,
} from '@adflow/shared/util-formatting';
import type {
	ListGMBTemplatesHistoryQuery,
	ViewHistoryGMBTemplateDto,
} from '@adflow/shared/util-history';
import {
	DEFAULT_HISTORY_GMB_TEMPLATE_SORT,
	DEFAULT_HISTORY_GMB_TEMPLATE_SORT_DIR,
} from '@adflow/shared/util-history';

import { GMBHistoryRowExpanded } from './GMBHistoryRowExpanded';

interface GMBTemplateHistoryProps {
	templateId: number;
}

export function GMBTemplateHistory({ templateId }: GMBTemplateHistoryProps) {
	const { t } = useTranslation('common');

	const [openRows, setOpenRows] = useState<(number | string)[]>([]);

	const [sort, setSort] = useState<ListGMBTemplatesHistoryQuery>({
		sort: DEFAULT_HISTORY_GMB_TEMPLATE_SORT,
		sortDirection: DEFAULT_HISTORY_GMB_TEMPLATE_SORT_DIR,
	});

	const { data: historyData, isPending } = useGMBTemplateHistoryQuery(
		templateId,
		sort
	);

	const historyDataColumns = useGMBHistoryDataColumns();

	const handleSortChange = useCallback((newSort: Partial<SortParams>) => {
		setSort((prev) => ({
			...prev,
			...(newSort as Partial<ListGMBTemplatesHistoryQuery>),
		}));
	}, []);

	const onRowClick = useCallback<
		NonNullable<DataTableFullProps<ViewHistoryGMBTemplateDto>['onRowClick']>
	>((_row, toggleRowExpanded, _e, _ctrlClick) => {
		toggleRowExpanded?.();
	}, []);

	const expandedRowRender = (
		data: ViewHistoryGMBTemplateDto,
		toggleRowExpanded: () => void
	) => {
		return <GMBHistoryRowExpanded history={data} onClose={toggleRowExpanded} />;
	};

	const emptySection = useCallback(
		() => (
			<div
				css={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					height: '100%',
				}}
			>
				<EmptySection
					Icon={IconPages}
					iconStyle={{ height: 'unset' }}
					message={t('history.empty')}
				/>
			</div>
		),
		[t]
	);

	return (
		<div
			css={{
				flexGrow: 1,
				flexShrink: 1,
				minHeight: '0px',
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				height: '100%',
			}}
		>
			<DataTableFull
				{...historyDataColumns}
				sort={sort}
				setSort={handleSortChange}
				setOpenRows={setOpenRows}
				openRows={openRows}
				data={historyData ?? []}
				onRowClick={onRowClick}
				expandedRowRender={expandedRowRender}
				renderWhenDataEmpty={emptySection}
				itemType="google-my-business-template-history"
				isLoading={isPending}
			/>
		</div>
	);
}

const columnHelper = createColumnHelper<ViewHistoryGMBTemplateDto>();

export function useGMBHistoryDataColumns() {
	const { t } = useTranslation('common');
	const columnDef = useMemo<ColumnDef<ViewHistoryGMBTemplateDto>[]>(
		() => [
			columnHelper.accessor('user.name', {
				id: 'avatar',
				meta: {
					sticky: true,
					cellStyle: {
						paddingLeft: 16,
						paddingRight: 0,
					},
					disableOrdering: true,
				},
				enableSorting: false,
				size: 16 + 32 + 0,
				maxSize: 16 + 32 + 0,

				header: ({ column }) => <HeaderCell text="" column={column} />,
				cell: ({ getValue }) => {
					return <Avatar name={getValue()} />;
				},
			}),
			columnHelper.accessor('user.name', {
				id: 'user',
				meta: {
					name: t('history.columns.user'),
					sticky: true,
				},

				header: HeaderCell,
				cell: ({ getValue }) => <TruncatedTextCell value={getValue()} />,
			}),
			columnHelper.accessor('action', {
				id: 'action',
				meta: {
					name: t('history.columns.action'),
				},
				header: HeaderCell,
				cell: ({ getValue }) => (
					<TruncatedTextCell value={sentenceCase(getValue())} />
				),
			}),
			columnHelper.accessor('action', {
				id: 'success',
				meta: {
					name: t('history.columns.success'),
				},
				enableSorting: false,
				size: 45,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ row }) => {
					const data = row.original;
					if (data.action !== 'UPLOAD') {
						return null;
					}

					// Count successful uploads (status !== 'error' or similar)
					const successCount =
						data.newData.results?.filter(
							(result) =>
								result.status === 'success' || result.errors.length === 0
						).length ?? 0;

					return (
						<TruncatedTextCell
							textStyle={
								successCount ? { color: theme.colors.statuses.ACTIVE } : {}
							}
							align="right"
							value={successCount.toString()}
						/>
					);
				},
			}),
			columnHelper.accessor('action', {
				id: 'errors',
				meta: {
					name: t('history.columns.errors'),
				},
				enableSorting: false,
				size: 45,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ row }) => {
					const data = row.original;
					if (data.action !== 'UPLOAD') {
						return null;
					}

					// Count accounts with errors
					const accountsWithErrors =
						data.newData.results?.filter((result) => result.errors.length > 0)
							.length ?? 0;

					return (
						<TruncatedTextCell
							align="right"
							textStyle={
								accountsWithErrors
									? { color: theme.colors.statuses.DELETED }
									: {}
							}
							value={accountsWithErrors.toString()}
						/>
					);
				},
			}),
			columnHelper.accessor('createdAt', {
				id: 'createdAt',
				meta: {
					name: t('history.columns.when'),
				},
				sortDescFirst: true,

				header: ({ column }) => <HeaderCell column={column} align="right" />,
				cell: ({ getValue }) => (
					<TruncatedTextCell
						value={formatDateAndTime(getValue())}
						align="right"
					/>
				),
			}),
			columnHelper.accessor('origin', {
				id: 'origin',
				meta: {
					name: t('history.columns.origin'),
				},
				enableSorting: false,
				size: 100,
				header: HeaderCell,
				cell: ({ getValue }) => {
					const origin = getValue();

					if (!origin) {
						return null;
					}

					return <TruncatedTextCell value={origin} />;
				},
			}),
		],
		[t]
	);

	const columnProps = useColumnTemplate({
		columns: columnDef,
		itemType: 'google-my-business-template-history',
	});

	return columnProps;
}
